import { useState, useRef } from "react";
import { Plus, Search, MoreHorizontal, Pencil, Trash2, Image, Video, X, Loader2, Package, ShoppingCart, DollarSign } from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmptyState } from "@/components/ui/empty-state";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useBusiness } from "@/hooks/use-business";
import { useProducts } from "@/hooks/use-products";
import { useProductOrders } from "@/hooks/use-product-orders";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const getCurrencySymbol = (currency: string): string => {
  const symbols: Record<string, string> = {
    USD: "$", EUR: "€", GBP: "£", GHS: "₵", NGN: "₦", KES: "KSh",
  };
  return symbols[currency] || currency;
};

const formatPrice = (amount: number | null | undefined, currency = "GHS") =>
  `${getCurrencySymbol(currency)}${(Number(amount) || 0).toFixed(2)}`;

function titleCase(input: string) {
  return input.split(" ").filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

function orderStatusLabel(raw: string) {
  return titleCase(String(raw || "new").replaceAll("_", " "));
}

function OrderStatusChip({ status }: { status: string }) {
  const s = String(status || "new");
  const label = orderStatusLabel(s);
  const cls =
    s === "new" ? "bg-blue-50 text-blue-700 border-blue-200"
    : s === "confirmed" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : s === "preparing" ? "bg-amber-50 text-amber-700 border-amber-200"
    : s === "ready" ? "bg-violet-50 text-violet-700 border-violet-200"
    : s === "out_for_delivery" ? "bg-sky-50 text-sky-700 border-sky-200"
    : s === "delivered" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : s === "cancelled" ? "bg-slate-50 text-slate-600 border-slate-200"
    : "bg-muted text-muted-foreground border-border";
  return (
    <Badge variant="outline" className={`whitespace-nowrap text-xs ${cls}`}>
      {label}
    </Badge>
  );
}

function OrderFulfillmentChip({ isDelivery }: { isDelivery: boolean }) {
  return (
    <Badge
      variant="outline"
      className={`whitespace-nowrap text-xs ${
        isDelivery ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "bg-slate-50 text-slate-700 border-slate-200"
      }`}
    >
      {isDelivery ? "Delivery" : "Pickup"}
    </Badge>
  );
}

type LocalMedia = { file: File; preview: string; type: "image" | "video" };

export default function Sales() {
  const { business, loading: businessLoading } = useBusiness();
  const { products, loading: productsLoading, createProduct, updateProduct, archiveProduct } = useProducts();
  const { orders, loading: ordersLoading, updateOrderStatus } = useProductOrders();
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState<"products" | "orders">("products");

  const currency = business?.currency || "GHS";

  const [searchQuery, setSearchQuery] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Form state
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formPrice, setFormPrice] = useState("");
  const [formQuantity, setFormQuantity] = useState(""); // empty = unlimited
  const [formActive, setFormActive] = useState(true);
  const [formPosition, setFormPosition] = useState("1");
  const [formMedia, setFormMedia] = useState<LocalMedia[]>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetForm = () => {
    setEditingProductId(null);
    setFormName("");
    setFormDescription("");
    setFormPrice("");
    setFormQuantity("");
    setFormActive(true);
    setFormPosition("1");
    setFormMedia([]);
  };

  const openCreate = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const openEdit = (product: (typeof products)[0]) => {
    setEditingProductId(product.id);
    setFormName(product.name);
    setFormDescription(product.description || "");
    setFormPrice(product.price_amount?.toString() || "0");
    setFormQuantity(product.quantity_in_stock != null ? String(product.quantity_in_stock) : "");
    setFormActive(product.active);
    setFormPosition(String(product.position || 1));
    setFormMedia([]); // existing media shown separately via product.media_items
    setIsDialogOpen(true);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const newMedia: LocalMedia[] = [];
    Array.from(files).forEach((file) => {
      const isVideo = file.type.startsWith("video/");
      const isImage = file.type.startsWith("image/");
      if (isVideo || isImage) {
        newMedia.push({ file, preview: URL.createObjectURL(file), type: isVideo ? "video" : "image" });
      }
    });
    setFormMedia((prev) => [...prev, ...newMedia]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeLocalMedia = (index: number) => {
    setFormMedia((prev) => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      toast({ title: "Name is required", variant: "destructive" });
      return;
    }
    if (!business?.id) return;

    setIsSaving(true);
    try {
      // If editing, upload new media and merge with existing
      let mediaItems: { url: string; type: "image" | "video" }[] = [];

      // Keep existing media when editing
      if (editingProductId) {
        const existing = products.find((p) => p.id === editingProductId);
        mediaItems = existing?.media_items ? [...existing.media_items] : [];
      }

      // Upload any newly selected files
      for (const m of formMedia) {
        const ext = m.file.name.split(".").pop() || (m.type === "video" ? "mp4" : "jpg");
        const productId = editingProductId || "new";
        const path = `products/${business.id}/${productId}/${Date.now()}-${Math.random().toString(16).slice(2)}.${ext}`;
        const { error: upErr } = await supabase.storage.from("service-media").upload(path, m.file, {
          cacheControl: "3600",
          upsert: false,
        });
        if (upErr) {
          toast({ title: "Upload failed", description: upErr.message, variant: "destructive" });
          continue;
        }
        const { data: pub } = supabase.storage.from("service-media").getPublicUrl(path);
        if (pub?.publicUrl) {
          mediaItems.push({ url: pub.publicUrl, type: m.type });
        }
      }

      const primaryImage = mediaItems.find((m) => m.type === "image")?.url || null;
      const quantity = formQuantity.trim() === "" ? null : parseInt(formQuantity, 10);

      const payload = {
        name: formName.trim(),
        description: formDescription.trim() || null,
        price_amount: parseFloat(formPrice) || 0,
        currency,
        quantity_in_stock: Number.isFinite(quantity) ? quantity : null,
        active: formActive,
        position: parseInt(formPosition, 10) || 1,
        media_items: mediaItems.length > 0 ? mediaItems : null,
        image_url: primaryImage,
      };

      if (editingProductId) {
        await updateProduct(editingProductId, payload);
        toast({ title: "Product updated" });
      } else {
        const created = await createProduct(payload);
        // If we uploaded with "new" as placeholder path, those files are already uploaded with that path.
        // For a cleaner approach on new products we could re-upload, but since we include business_id
        // in the path the files are still isolated. Good enough for Phase 1.
        if (created) {
          toast({ title: "Product created" });
        }
      }

      setIsDialogOpen(false);
      resetForm();
    } catch (e: any) {
      toast({ title: "Save failed", description: e?.message || "Try again.", variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveExistingMedia = async (product: (typeof products)[0], index: number) => {
    const existing = product.media_items || [];
    const next = existing.filter((_, i) => i !== index);
    const primaryImage = next.find((m) => m.type === "image")?.url || null;
    try {
      await updateProduct(product.id, {
        media_items: next.length > 0 ? next : null,
        image_url: primaryImage,
      });
    } catch (e: any) {
      toast({ title: "Remove failed", description: e?.message, variant: "destructive" });
    }
  };

  const handleArchive = async (id: string) => {
    try {
      await archiveProduct(id);
      toast({ title: "Product archived" });
    } catch (e: any) {
      toast({ title: "Archive failed", description: e?.message, variant: "destructive" });
    }
  };

  const filtered = products.filter((p) =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const activeProducts = products.filter((p) => p.active);
  const totalRevenue = orders.reduce((sum, o) => sum + Number(o.total_amount || 0), 0);
  const isLoading = businessLoading || productsLoading || ordersLoading;

  // The product being edited (for showing existing media in dialog)
  const editingProduct = editingProductId ? products.find((p) => p.id === editingProductId) : null;
  const existingMedia = editingProduct?.media_items || [];

  return (
    <AppShell businessName={business?.name || "My Business"}>
      <PageHeader
        title="Sales"
        description="Manage products you sell to your customers"
        action={
          <Button size="pill" onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Add Product
          </Button>
        }
      />

      {/* Overview cards */}
      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Package className="h-4 w-4" />
            <span className="text-sm font-medium">Total Products</span>
          </div>
          <p className="mt-2 text-2xl font-bold text-foreground">{products.length}</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Package className="h-4 w-4" />
            <span className="text-sm font-medium">Active Products</span>
          </div>
          <p className="mt-2 text-2xl font-bold text-foreground">{activeProducts.length}</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <ShoppingCart className="h-4 w-4" />
            <span className="text-sm font-medium">Total Orders</span>
          </div>
          <p className="mt-2 text-2xl font-bold text-foreground">{orders.length}</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <DollarSign className="h-4 w-4" />
            <span className="text-sm font-medium">Total Revenue</span>
          </div>
          <p className="mt-2 text-2xl font-bold text-foreground">{formatPrice(totalRevenue, currency)}</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "products" | "orders")} className="space-y-4">
        <TabsList className="grid w-full max-w-sm grid-cols-2">
          <TabsTrigger value="products">Products</TabsTrigger>
          <TabsTrigger value="orders">Orders</TabsTrigger>
        </TabsList>

        <TabsContent value="products" className="space-y-4 mt-0">
      {/* Search */}
      <div>
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Products grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length > 0 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((product) => {
            const media = product.media_items || [];
            const primary = media[0] || (product.image_url ? { url: product.image_url, type: "image" as const } : null);
            return (
              <div key={product.id} className="bg-card rounded-xl border border-border shadow-soft p-5">
                <div className="flex items-start justify-between mb-3 gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    {/* Thumbnail */}
                    {primary?.url ? (
                      primary.type === "video" ? (
                        <video
                          src={primary.url}
                          className="h-14 w-14 rounded-lg object-cover border border-border bg-muted flex-shrink-0"
                          muted
                          playsInline
                          preload="metadata"
                        />
                      ) : (
                        <img
                          src={primary.url}
                          alt={product.name}
                          className="h-14 w-14 rounded-lg object-cover border border-border bg-muted flex-shrink-0"
                          loading="lazy"
                        />
                      )
                    ) : (
                      <div className="h-14 w-14 rounded-lg border border-border bg-muted flex items-center justify-center flex-shrink-0">
                        <Package className="h-6 w-6 text-muted-foreground" />
                      </div>
                    )}

                    <div className="min-w-0">
                      <h3 className="font-semibold text-foreground truncate">{product.name}</h3>
                      {product.description ? (
                        <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">{product.description}</p>
                      ) : null}
                    </div>
                  </div>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEdit(product)}>
                        <Pencil className="mr-2 h-4 w-4" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem className="text-destructive" onClick={() => handleArchive(product.id)}>
                        <Trash2 className="mr-2 h-4 w-4" />
                        Archive
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-border">
                  <div className="flex items-center gap-3 text-sm">
                    <span className="font-semibold text-foreground">
                      {formatPrice(product.price_amount, product.currency || currency)}
                    </span>
                    {product.quantity_in_stock != null ? (
                      product.quantity_in_stock <= 0 ? (
                        <span className="text-destructive font-medium">Sold out</span>
                      ) : (
                        <span className="text-muted-foreground">
                          {product.quantity_in_stock} in stock
                        </span>
                      )
                    ) : (
                      <span className="text-muted-foreground">Unlimited</span>
                    )}
                  </div>
                  <Badge variant={product.active ? "default" : "secondary"} className="text-xs">
                    {product.active ? "Active" : "Inactive"}
                  </Badge>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-card rounded-xl border border-border shadow-soft">
          <EmptyState
            title="No products yet"
            description="Add your first product to start selling to customers"
            action={{ label: "Add Product", onClick: openCreate }}
          />
        </div>
      )}
        </TabsContent>

        <TabsContent value="orders" className="mt-0 space-y-4">
          {orders.length === 0 ? (
            <div className="rounded-xl border border-border bg-card p-12 text-center">
              <ShoppingCart className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 font-semibold text-foreground">No orders yet</h3>
              <p className="mt-1 text-sm text-muted-foreground">Product orders from your booking page will appear here.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((order) => {
                const items = order.items || [];
                const isPast = ["delivered", "cancelled"].includes(String(order.status || "new"));
                return (
                  <div key={order.id} className="rounded-xl border border-border bg-card p-5 space-y-4">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex items-center flex-wrap gap-2">
                          <span className="font-semibold text-foreground">#{order.confirmation_code}</span>
                          <OrderFulfillmentChip isDelivery={order.fulfillment_type === "delivery"} />
                          <OrderStatusChip status={String(order.status || "new")} />
                        </div>
                        <p className="text-sm text-muted-foreground mt-2">{order.customer_name}</p>
                        {order.customer_phone ? (
                          <p className="text-sm text-muted-foreground">{order.customer_phone}</p>
                        ) : null}
                        {order.fulfillment_type === "delivery" && order.delivery_address ? (
                          <p className="text-sm text-muted-foreground mt-1">{order.delivery_address}</p>
                        ) : null}
                      </div>
                      <div className="w-full lg:w-[200px] shrink-0">
                        <div className="text-xs font-medium text-muted-foreground mb-1">Update status</div>
                        <select
                          value={String(order.status || "new")}
                          disabled={isPast}
                          onChange={async (e) => {
                            const v = String(e.target.value);
                            try {
                              await updateOrderStatus(order.id, v);
                              toast({ title: "Status updated" });
                              void (async () => {
                                try {
                                  await (supabase as any).functions.invoke("send-email", {
                                    body: { type: "product_order_status_changed", orderId: order.id, newStatus: v },
                                  });
                                } catch {
                                  // ignore
                                }
                              })();
                            } catch (err: any) {
                              toast({
                                title: "Failed to update",
                                description: err?.message || "Try again.",
                                variant: "destructive",
                              });
                            }
                          }}
                          className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          aria-label="Update status"
                        >
                          <option value="new">New</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="preparing">Preparing</option>
                          <option value="ready">Ready</option>
                          <option value="out_for_delivery">Out for delivery</option>
                          <option value="delivered">Delivered</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </div>
                    </div>
                    <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-2">
                      <div className="text-sm font-semibold text-foreground">Items</div>
                      {items.map((item) => (
                        <div key={item.id} className="flex items-start justify-between gap-4 text-sm">
                          <div>
                            <span className="font-medium text-foreground">{item.product_name}</span>
                            <span className="text-muted-foreground ml-1">× {item.quantity}</span>
                          </div>
                          <span className="font-semibold text-foreground whitespace-nowrap">
                            {formatPrice(item.line_total, order.currency)}
                          </span>
                        </div>
                      ))}
                      <div className="flex items-center justify-between pt-2 border-t border-border">
                        <span className="font-semibold text-foreground">Total</span>
                        <span className="font-semibold text-foreground">{formatPrice(order.total_amount, order.currency)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add / Edit Product Dialog */}
      <Dialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          if (!open) resetForm();
          setIsDialogOpen(open);
        }}
      >
        <DialogContent
          className="sm:max-w-md w-[calc(100%-2rem)] max-h-[90vh] p-0 gap-0 overflow-hidden rounded-2xl flex flex-col"
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
            <DialogTitle>{editingProductId ? "Edit Product" : "Add Product"}</DialogTitle>
          </DialogHeader>

          <div className="overflow-y-auto px-6 pb-4 flex-1 min-h-0">
            <div className="space-y-5">

              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="prod-name">Product Name</Label>
                <Input
                  id="prod-name"
                  placeholder="e.g., Hair Growth Oil, Face Serum"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                />
              </div>

              {/* Price */}
              <div className="space-y-2">
                <Label htmlFor="prod-price">Price ({currency})</Label>
                <Input
                  id="prod-price"
                  type="number"
                  placeholder="e.g., 50"
                  min={0}
                  value={formPrice}
                  onChange={(e) => setFormPrice(e.target.value)}
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="prod-desc">Description</Label>
                <Textarea
                  id="prod-desc"
                  placeholder="Describe the product — size, ingredients, usage, etc."
                  rows={3}
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                />
              </div>

              {/* Quantity in Stock */}
              <div className="space-y-2">
                <Label htmlFor="prod-qty">Quantity in Stock</Label>
                <Input
                  id="prod-qty"
                  type="number"
                  placeholder="Leave empty for unlimited"
                  min={0}
                  value={formQuantity}
                  onChange={(e) => setFormQuantity(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Leave empty if you don't want to track stock. Set to 0 to mark as out of stock.
                </p>
              </div>

              {/* Photos & Videos */}
              <div className="space-y-3">
                <Label>Photos &amp; Videos</Label>
                <p className="text-xs text-muted-foreground">
                  Add photos or short videos to showcase this product.
                </p>

                {/* Existing media (when editing) */}
                {existingMedia.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {existingMedia.map((m, idx) => (
                      <div key={m.url} className="relative group">
                        {m.type === "video" ? (
                          <div className="relative w-20 h-20 rounded-lg border border-border overflow-hidden bg-muted">
                            <video src={m.url} className="w-full h-full object-cover" muted playsInline preload="metadata" />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                              <Video className="h-6 w-6 text-white" />
                            </div>
                          </div>
                        ) : (
                          <img src={m.url} alt="" className="w-20 h-20 object-cover rounded-lg border border-border" />
                        )}
                        <button
                          type="button"
                          onClick={() => editingProduct && handleRemoveExistingMedia(editingProduct, idx)}
                          className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* New local files preview */}
                {formMedia.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formMedia.map((m, idx) => (
                      <div key={idx} className="relative group">
                        {m.type === "video" ? (
                          <div className="relative w-20 h-20 rounded-lg border border-border overflow-hidden bg-muted">
                            <video src={m.preview} className="w-full h-full object-cover" muted playsInline />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                              <Video className="h-6 w-6 text-white" />
                            </div>
                          </div>
                        ) : (
                          <img src={m.preview} alt="" className="w-20 h-20 object-cover rounded-lg border border-border" />
                        )}
                        <button
                          type="button"
                          onClick={() => removeLocalMedia(idx)}
                          className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  className="hidden"
                  onChange={handleFileSelect}
                />
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Image className="mr-2 h-4 w-4" />
                  Add Photos or Videos
                </Button>
              </div>

              {/* Active toggle */}
              <div className="flex items-center justify-between rounded-lg border border-border p-4">
                <div>
                  <div className="text-sm font-medium text-foreground">Product Status</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Inactive products won't appear to customers
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={formActive ? "default" : "secondary"} className="text-xs">
                    {formActive ? "Active" : "Inactive"}
                  </Badge>
                  <Switch checked={formActive} onCheckedChange={setFormActive} />
                </div>
              </div>

              {/* Position */}
              <div className="space-y-2">
                <Label>Display Order</Label>
                <Select value={formPosition} onValueChange={setFormPosition}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select position" />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((pos) => (
                      <SelectItem key={pos} value={String(pos)}>
                        Position {pos}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t border-border bg-background flex-row gap-3 flex-shrink-0">
            <Button
              variant="outline"
              onClick={() => { setIsDialogOpen(false); resetForm(); }}
              className="flex-1 rounded-lg"
            >
              Cancel
            </Button>
            <Button onClick={handleSave} className="flex-1 rounded-lg" disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                editingProductId ? "Save Changes" : "Add Product"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
